import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, Edit, Trash, Eye, Search, Building, Mail, Phone, ArrowUp, ArrowDown, Download } from 'lucide-react';
import LocationSelector from '../components/LocationSelector';
import * as XLSX from 'xlsx';
import { companiesAPI } from '../lib/api';
import { useTranslation } from '../lib/i18n';

const Companies = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    tax_number: '',
    description: '',
    latitude: '',
    longitude: '',
    country: '',
    state: ''
  });

  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check if we came from CompanyDetail with edit state
  useEffect(() => {
    if (location.state?.editCompany) {
      handleEdit(location.state.editCompany);
      // Clear the state to prevent re-editing on refresh
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const data = await companiesAPI.getAll();
      setCompanies(Array.isArray(data) ? data : []);
      setError('');
    } catch (err) {
      console.error('Şirketler getirilemedi:', err);
      setError(err.message || t('companies.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLocationSelect = (location) => {
    setFormData(prev => ({
      ...prev,
      country: location.country,
      state: location.stateName || location.state // State adını kullan
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      phone: '',
      email: '',
      website: '',
      tax_number: '',
      description: '',
      latitude: '',
      longitude: '',
      country: '',
      state: ''
    });
    setEditingCompany(null);
    setShowAddForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Latitude ve longitude'u number'a çevir
      const submitData = {
        ...formData,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null
      };

      let result;
      if (editingCompany) {
        result = await companiesAPI.update(editingCompany.id, submitData);
        setCompanies(companies.map(company => 
          company.id === editingCompany.id ? result : company
        ));
      } else {
        result = await companiesAPI.create(submitData);
        setCompanies([...companies, result]);
      }
      resetForm();
      setError('');
    } catch (err) {
      console.error('Şirket işlemi hatası:', err);
      setError(err.message || t('companies.operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    
    // Enlem ve boylam değerlerini hassasiyetle string'e çevir
    const formatCoordinate = (value) => {
      if (value === null || value === undefined) return '';
      if (typeof value === 'string') return value;
      return String(value);
    };
    
    setFormData({
      name: company.name,
      address: company.address || '',
      phone: company.phone || '',
      email: company.email || '',
      website: company.website || '',
      tax_number: company.tax_number || '',
      description: company.description || '',
      latitude: formatCoordinate(company.latitude),
      longitude: formatCoordinate(company.longitude),
      country: company.country || '',
      state: company.state || ''
    });
    
    setShowAddForm(true);
  };

  const handleDelete = async (companyId) => {
    if (!window.confirm(t('companies.deleteConfirm'))) {
      return;
    }

    try {
      setLoading(true);
      await companiesAPI.delete(companyId);
      setCompanies(companies.filter(company => company.id !== companyId));
      setError('');
    } catch (err) {
      console.error('Şirket silme hatası:', err);
      setError(err.message || t('companies.deleteError'));
    } finally {
      setLoading(false);
    }
  };

  const handleView = (company) => {
    navigate(`/companies/${company.id}`);
  };

  const handleSelectCompany = (companyId) => {
    setSelectedCompanies(prev => 
      prev.includes(companyId) 
        ? prev.filter(id => id !== companyId)
        : [...prev, companyId]
    );
  };

  const handleSelectAll = () => {
    const filteredIds = filteredCompanies.map(company => company.id);
    const allFilteredSelected = filteredIds.every(id => selectedCompanies.includes(id));
    
    if (allFilteredSelected) {
      // Filtrelenmiş listedeki seçimleri kaldır
      setSelectedCompanies(prev => prev.filter(id => !filteredIds.includes(id)));
    } else {
      // Filtrelenmiş listedeki tümünü seç
      setSelectedCompanies(prev => {
        const newSelection = [...prev];
        filteredIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCompanies.length === 0) {
      setError(t('companies.selectToDelete'));
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError(t('companies.loginRequired'));
      return;
    }

    const confirmMessage = t('companies.bulkDeleteConfirm', { count: selectedCompanies.length });
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await fetch('http://localhost:5001/api/companies/bulk-delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ids: selectedCompanies })
      });

      const data = await response.json();

      if (response.ok) {
        setCompanies(companies.filter(company => !selectedCompanies.includes(company.id)));
        setSelectedCompanies([]);
        setError('');
        alert(data.message || `${selectedCompanies.length} şirket başarıyla silindi`);
      } else {
        if (response.status === 401 || response.status === 403) {
          setError(t('companies.sessionExpired'));
          localStorage.removeItem('token');
        } else {
          setError(data.error || t('companies.bulkDeleteFailed'));
        }
      }
    } catch (err) {
      setError(t('companies.connectionError'));
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? <ArrowUp size={14} className="inline ml-1" /> : <ArrowDown size={14} className="inline ml-1" />;
  };

  // XLSX Export fonksiyonu
  const handleExport = () => {
    const dataToExport = filteredCompanies.map(company => ({
      [t('companies.name')]: company.name || '',
      [t('companies.address')]: company.address || '',
      [t('companies.phone')]: company.phone || '',
      [t('companies.email')]: company.email || '',
      [t('companies.website')]: company.website || '',
      [t('companies.taxNumber')]: company.tax_number || '',
      [t('companies.description')]: company.description || '',
      [t('companies.latitude')]: company.latitude || '',
      [t('companies.longitude')]: company.longitude || '',
      [t('companies.country')]: company.country || '',
      [t('companies.city')]: company.state || ''
    }));

    // XLSX oluştur
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('companies.sheetName'));
    
    // Dosyayı indir
    XLSX.writeFile(wb, `sirketler_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    alert(t('companies.exportSuccess'));
  };

  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (company.address && company.address.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (company.phone && company.phone.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (company.email && company.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (company.tax_number && company.tax_number.toLowerCase().includes(searchQuery.toLowerCase()))
  ).sort((a, b) => {
    if (!sortColumn) return 0;
    
    let aValue, bValue;
    
    switch (sortColumn) {
      case 'name':
        aValue = a.name?.toLowerCase() || '';
        bValue = b.name?.toLowerCase() || '';
        break;
      case 'email':
        aValue = a.email?.toLowerCase() || '';
        bValue = b.email?.toLowerCase() || '';
        break;
      case 'phone':
        aValue = a.phone?.toLowerCase() || '';
        bValue = b.phone?.toLowerCase() || '';
        break;
      case 'tax_number':
        aValue = a.tax_number?.toLowerCase() || '';
        bValue = b.tax_number?.toLowerCase() || '';
        break;
      case 'address':
        aValue = a.address?.toLowerCase() || '';
        bValue = b.address?.toLowerCase() || '';
        break;
      default:
        return 0;
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-10 p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Page Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('companies.title')}</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2 text-lg">{t('companies.subtitle')}</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExport}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl flex items-center space-x-2 shadow-sm hover:shadow-md transition-all duration-200"
            >
              <Download className="h-5 w-5" />
              <span className="font-medium">{t('companies.export')}</span>
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl flex items-center space-x-2 shadow-sm hover:shadow-md transition-all duration-200"
            >
              <Plus className="h-5 w-5" />
              <span className="font-medium">{t('companies.addNew')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Arama */}
      <div className="mb-8">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
          <input
            type="text"
            placeholder={t('companies.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Hata Mesajı */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Şirket Ekleme/Düzenleme Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-200 dark:border-gray-700">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingCompany ? t('companies.editTitle') : t('companies.addTitle')}
              </h2>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('companies.name')} *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('companies.phone')}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('companies.email')}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('companies.website')}
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('companies.taxNumber')}
                  </label>
                  <input
                    type="text"
                    name="tax_number"
                    value={formData.tax_number}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('companies.address')}
                </label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('companies.description')}
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
              </div>
              
              {/* Konum Seçimi */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 uppercase tracking-wide">{t('companies.locationInfo')}</h3>
                
                <LocationSelector
                   key={`${editingCompany?.id || 'new'}`}
                   onLocationSelect={handleLocationSelect}
                   initialLocation={{
                     country: formData.country,
                     state: formData.state
                   }}
                 />
                
                {/* Koordinat Seçimi */}
                <div className="mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('companies.latitude')}
                      </label>
                      <input
                        type="text"
                        name="latitude"
                        value={formData.latitude}
                        onChange={handleInputChange}
                        placeholder={t('companies.latitudePlaceholder')}
                        pattern="^-?\d+(\.\d+)?$"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('companies.longitude')}
                      </label>
                      <input
                        type="text"
                        name="longitude"
                        value={formData.longitude}
                        onChange={handleInputChange}
                        placeholder={t('companies.longitudePlaceholder')}
                        pattern="^-?\d+(\.\d+)?$"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      />
                    </div>
                  </div>
                  <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                    💡 {t('coordinates.copyFromMaps')}
                  </p>
                </div>
              </div>
              
              {/* Modal Footer */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  {t('companies.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors"
                >
                  {loading ? t('companies.saving') : (editingCompany ? t('companies.update') : t('companies.add'))}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Şirket Listesi - WISECP Style */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        {selectedCompanies.length > 0 && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-900/30 px-4 py-2 flex items-center justify-between">
            <span className="text-sm text-blue-700 dark:text-blue-300">
              {t('companies.selectedCount', { count: selectedCompanies.length })}
            </span>
            <button
              onClick={handleBulkDelete}
              className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded"
            >
              {t('companies.deleteSelected')}
            </button>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={filteredCompanies.length > 0 && filteredCompanies.every(company => selectedCompanies.includes(company.id))}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                  onClick={() => handleSort('name')}
                >
                  {t('companies.name')} {getSortIcon('name')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                  {t('companies.contact')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                  {t('companies.taxNumber')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                  {t('companies.status')}
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide w-28">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {filteredCompanies.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 text-sm">
                    {searchQuery ? t('companies.noSearchResults') : t('companies.noCompanies')}
                  </td>
                </tr>
              ) : (
                filteredCompanies.map((company) => (
                  <tr key={company.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedCompanies.includes(company.id)}
                        onChange={() => handleSelectCompany(company.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                          <Building className="h-5 w-5 text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-gray-900 dark:text-white">
                            {company.name}
                          </div>
                          {company.address && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px]">
                              {company.address}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        {company.phone && (
                          <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <Phone size={14} className="text-gray-400 flex-shrink-0" />
                            <span>{company.phone}</span>
                          </div>
                        )}
                        {company.email && (
                          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                            <Mail size={14} className="text-gray-400 flex-shrink-0" />
                            <span className="truncate">{company.email}</span>
                          </div>
                        )}
                        {!company.phone && !company.email && (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {company.tax_number || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold ${
                        company.is_active
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                        <div className={`w-1.5 h-1.5 rounded-full ${company.is_active ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        {company.is_active ? t('companies.statusActive') : t('companies.statusInactive')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleView(company)}
                          className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                          title={t('companies.view')}
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => handleEdit(company)}
                          className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded transition-colors"
                          title={t('companies.edit')}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(company.id)}
                          className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                          title={t('companies.delete')}
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Tablo Alt Bilgisi */}
        <div className="bg-gray-50 dark:bg-gray-700/30 border-t border-gray-200 dark:border-gray-700 px-4 py-2 flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
          <span>
            {filteredCompanies.length} {t('companies.resultsFound')}
          </span>
          <div className="flex items-center gap-2">
            <button className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50" disabled>
              {t('companies.previous')}
            </button>
            <span className="px-2 py-1 bg-blue-600 text-white rounded">1</span>
            <button className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50" disabled>
              {t('companies.next')}
            </button>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Companies;
